import React, { useEffect, useState } from 'react';
import {
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { supabase } from '../supabaseClient.ts';

type FlagReason =
  | 'incorrect_answer'
  | 'bad_solution'
  | 'typo'
  | 'unclear'
  | 'formatting'
  | 'duplicate'
  | 'other';

type Props = {
  questionId?: string | null;
  studentId?: string | null;
  companyId?: string | null;
  showSave?: boolean;
  showFlag?: boolean;
};

const QuestionActionButtons: React.FC<Props> = ({
  questionId,
  studentId,
  companyId = null,
  showSave = true,
  showFlag = true,
}) => {
  const [isSaved, setIsSaved] = useState(false);
  const [hasOpenFlags, setHasOpenFlags] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState<FlagReason>('incorrect_answer');
  const [flagNote, setFlagNote] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  useEffect(() => {
    if (!showSave || !questionId || !studentId) {
      setIsSaved(false);
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .from('student_saved_questions')
        .select('question_id')
        .eq('student_user_id', studentId)
        .eq('question_id', questionId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading saved-question state:', error);
      }
      setIsSaved(!!data);
    })();
  }, [questionId, studentId, showSave]);

  useEffect(() => {
    if (!showFlag || !questionId) {
      setHasOpenFlags(false);
      return;
    }

    (async () => {
      const { data: openFlags, error: openFlagsError } = await supabase
        .from('question_flags')
        .select('id')
        .eq('question_id', questionId)
        .eq('status', 'open')
        .limit(1);
      if (!openFlagsError && openFlags && openFlags.length > 0) {
        setHasOpenFlags(true);
      } else {
        setHasOpenFlags(false);
      }
    })();
  }, [questionId, showFlag]);

  const toggleSaved = async () => {
    if (!questionId || !studentId || !showSave) return;

    try {
      if (isSaved) {
        const { error } = await supabase
          .from('student_saved_questions')
          .delete()
          .eq('student_user_id', studentId)
          .eq('question_id', questionId);
        if (error) throw error;
        setIsSaved(false);
        setSnack({ open: true, msg: 'Removed from saved questions.', sev: 'success' });
      } else {
        const { error } = await supabase
          .from('student_saved_questions')
          .insert({
            student_user_id: studentId,
            question_id: questionId,
          });
        if (error) throw error;
        setIsSaved(true);
        setSnack({ open: true, msg: 'Saved for later.', sev: 'success' });
      }
    } catch (e: any) {
      console.error('Error toggling saved question:', e);
      setSnack({
        open: true,
        msg: e?.message ?? 'Could not update saved question.',
        sev: 'error',
      });
    }
  };

  const openFlagDialog = () => {
    if (!questionId || !showFlag) return;
    setFlagReason('incorrect_answer');
    setFlagNote('');
    setFlagOpen(true);
  };

  const submitFlag = async () => {
    if (!questionId || !showFlag) return;
    setFlagSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('question_flags')
        .insert([
          {
            question_id: questionId,
            reporter_id: user?.id ?? null,
            company_id: companyId,
            reason: flagReason,
            note: flagNote.trim() || null,
            status: 'open',
          },
        ]);
      if (error) throw error;

      setHasOpenFlags(true);
      setFlagOpen(false);
      setSnack({ open: true, msg: 'Thanks — this question has been flagged.', sev: 'success' });
    } catch (e: any) {
      setSnack({
        open: true,
        msg: e?.message ?? 'Failed to flag question.',
        sev: 'error',
      });
    } finally {
      setFlagSubmitting(false);
    }
  };

  return (
    <>
      {showFlag && (
        <Tooltip title={hasOpenFlags ? 'This question has been flagged' : 'Flag a problem'}>
          <span>
            <Button
              startIcon={<FlagOutlinedIcon />}
              variant={hasOpenFlags ? 'contained' : 'outlined'}
              color="warning"
              onClick={openFlagDialog}
              disabled={!questionId}
              sx={{
                borderColor: hasOpenFlags ? 'warning.main' : undefined,
                fontWeight: hasOpenFlags ? 600 : undefined,
              }}
            >
              {hasOpenFlags ? 'Flagged' : 'Flag'}
            </Button>
          </span>
        </Tooltip>
      )}

      {showSave && (
        <Tooltip title={isSaved ? 'Remove from saved' : 'Save this question'}>
          <span>
            <Button
              startIcon={isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              variant={isSaved ? 'contained' : 'outlined'}
              color="primary"
              onClick={toggleSaved}
              disabled={!studentId || !questionId}
            >
              {isSaved ? 'Saved' : 'Save'}
            </Button>
          </span>
        </Tooltip>
      )}

      <Dialog open={flagOpen} onClose={() => setFlagOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Flag this question</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <RadioGroup
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value as FlagReason)}
            >
              {[
                { value: 'incorrect_answer', label: 'Incorrect answer' },
                { value: 'bad_solution', label: 'Wrong/poor solution' },
                { value: 'typo', label: 'Typo or grammar' },
                { value: 'unclear', label: 'Unclear question' },
                { value: 'formatting', label: 'Formatting/LaTeX issue' },
                { value: 'duplicate', label: 'Duplicate question' },
                { value: 'other', label: 'Other' },
              ].map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio />}
                  label={r.label}
                />
              ))}
            </RadioGroup>
            <TextField
              label="Details (optional)"
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              multiline
              minRows={3}
              placeholder="Explain what’s wrong or how to reproduce the issue..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagOpen(false)}>Cancel</Button>
          <Button
            onClick={submitFlag}
            variant="contained"
            startIcon={<FlagOutlinedIcon />}
            disabled={flagSubmitting}
          >
            {flagSubmitting ? 'Submitting...' : 'Submit Flag'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.sev}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuestionActionButtons;
